// 005 slice / 007 (P-HOME-1, P-HOME-2): the parent Home hero. "Shopping for" chips list the linked
// children; the selected child's school's live banners show in the hero carousel (fallback: the
// school's name). Choosing a child at another school switches the banners, and the header follows
// (004 FR-026). Categories and booklists come with the rest of 005.
import { Link } from "expo-router";
import { type ComponentType, useEffect, useState } from "react";
import { Platform, ScrollView, StyleSheet, View } from "react-native";

import { selectStudent, useSelectedStudent } from "@/profile/selection";
import { useParentData } from "@/profile/useParentData";
import { standardClassName } from "@/profile/viewModels";
import type { ColorRole } from "@/theme/tokens";
import { makeStyles } from "@/theme/makeStyles";
import { useChromeKind } from "@/shell/chrome/useChromeKind";
import { useTheme } from "@/theme/useTheme";
import { BookIcon } from "@/ui/icons/BookIcon";
import { CheckIcon } from "@/ui/icons/CheckIcon";
import type { IconProps } from "@/ui/icons/LineIcon";
import { PencilIcon } from "@/ui/icons/PencilIcon";
import { ShirtIcon } from "@/ui/icons/ShirtIcon";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

import { type HeroBanner, HeroCarousel } from "./HeroCarousel";
import { useHomeClient } from "./homeApi";

/**
 * Per school, whether its last banner load found any (this session only). Sizes the hero's loading
 * placeholder to what will replace it, so the page doesn't jump.
 */
const hadBanners = new Map<string, boolean>();

export const HOME_COPY = {
  shoppingFor: "Shopping for",
  heroFallback: "Books & stationery for the new session",
  categories: "Shop by category",
  comingSoon: "Coming soon",
  /** P-HOME-5: web footer. */
  footer: "Supplied and delivered by Akaiva Edu Pvt Ltd",
  chip: (name: string, classLevel: string, section: string) =>
    `${name} · ${standardClassName(classLevel)} ${section}`,
} as const;

type Category = {
  id: string;
  label: string;
  Icon: ComponentType<IconProps>;
  tint: ColorRole;
  soft: ColorRole;
  /** Not orderable yet (PRD non-goal): shown, never pressable. */
  soon?: boolean;
};

// P-HOME-3: the three categories. Books and stationery open Shop; uniforms are "Coming soon".
const CATEGORIES: readonly Category[] = [
  {
    id: "books",
    label: "School books",
    Icon: BookIcon,
    tint: "tintAmber",
    soft: "tintAmberSoft",
  },
  {
    id: "stationery",
    label: "Stationery",
    Icon: PencilIcon,
    tint: "tintGreen",
    soft: "tintGreenSoft",
  },
  {
    id: "uniforms",
    label: "Uniforms",
    Icon: ShirtIcon,
    tint: "tintPink",
    soft: "tintPinkSoft",
    soon: true,
  },
];

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    scroll: { flex: 1 },
    page: {
      width: "100%",
      maxWidth: 1080,
      alignSelf: "center",
      paddingHorizontal: t.space.lg,
      paddingTop: t.space.lg,
      paddingBottom: t.space.x3l,
      gap: t.space.x2l,
    },
    section: { gap: t.space.md },
    children: { flexDirection: "row", flexWrap: "wrap", gap: t.space.sm },
    child: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: t.space.smd,
      minWidth: 200,
      paddingVertical: t.space.md,
      paddingLeft: t.space.md,
      paddingRight: t.space.xl,
      borderRadius: t.radius.card,
      borderWidth: t.borderWidth.input,
      borderColor: t.color.border,
      backgroundColor: t.color.surface,
    },
    childOn: {
      borderColor: t.color.primary,
      backgroundColor: t.color.primarySoft,
    },
    avatar: {
      width: t.size.iconButton,
      height: t.size.iconButton,
      borderRadius: t.radius.pill,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: t.color.surfaceAlt,
    },
    avatarOn: { backgroundColor: t.color.primary },
    childText: { gap: t.space.xxs / 2 },
    tiles: { flexDirection: "row", gap: t.space.smd },
    tile: {
      flex: 1,
      alignItems: "flex-start",
      justifyContent: "flex-start",
      gap: t.space.smd,
      padding: t.space.md,
      borderRadius: t.radius.card,
      borderWidth: t.borderWidth.card,
      borderColor: t.color.border,
      backgroundColor: t.color.surface,
    },
    // Desktop: icon beside the label, so wide tiles don't leave an empty block under it.
    tileWide: {
      flexDirection: "row",
      alignItems: "center",
      padding: t.space.lg,
      gap: t.space.md,
    },
    // Not orderable: no card surface, a dashed edge and a muted icon, so it never reads as tappable.
    tileSoon: {
      backgroundColor: "transparent",
      borderStyle: "dashed",
      borderWidth: t.borderWidth.input,
    },
    tileText: { alignSelf: "stretch", flexShrink: 1, justifyContent: "center" },
    hover: { borderColor: t.color.primary },
    footer: {
      paddingTop: t.space.lg,
      borderTopWidth: t.borderWidth.card,
      borderTopColor: t.color.border,
    },
    tileIcon: {
      width: 56,
      height: 56,
      borderRadius: t.radius.button,
      alignItems: "center",
      justifyContent: "center",
    },
  }),
);

function CategoryTile({ category }: { category: Category }) {
  const styles = useStyles();
  const theme = useTheme();
  const wide = useChromeKind() === "desktop";
  const { Icon, label, soon } = category;
  const body = (
    <>
      <View
        style={[
          styles.tileIcon,
          {
            backgroundColor: soon
              ? theme.color.surfaceAlt
              : theme.color[category.soft],
          },
        ]}
      >
        <Icon
          color={soon ? theme.color.textMuted : theme.color[category.tint]}
          size={theme.size.icon.lg}
        />
      </View>
      <View style={styles.tileText}>
        <Text weight="semiBold">{label}</Text>
        {soon ? (
          <Text size="sm" tone="muted">
            {HOME_COPY.comingSoon}
          </Text>
        ) : null}
      </View>
    </>
  );
  if (soon) {
    return (
      <View
        style={[styles.tile, wide && styles.tileWide, styles.tileSoon]}
        testID={`home-category-${category.id}`}
        accessible
        accessibilityLabel={`${label}, ${HOME_COPY.comingSoon}`}
      >
        {body}
      </View>
    );
  }
  return (
    <Link href="/shop" asChild>
      <PressableTarget
        accessibilityRole="link"
        accessibilityLabel={label}
        // Link asChild hands the style to the DOM on the web: it must be one object, not an array.
        style={StyleSheet.flatten([styles.tile, wide && styles.tileWide])}
        hoveredStyle={styles.hover}
        testID={`home-category-${category.id}`}
      >
        {body}
      </PressableTarget>
    </Link>
  );
}

export function HomeScreen() {
  const styles = useStyles();
  const theme = useTheme();
  const client = useHomeClient();
  const { students } = useParentData();
  const selectedId = useSelectedStudent();
  const linked = (students.data ?? []).filter(
    (s) => s.linked && s.school !== null,
  );
  const selected = linked.find((s) => s.id === selectedId) ?? linked[0] ?? null;
  const schoolId = selected?.school?.id ?? null;
  const [banners, setBanners] = useState<{
    schoolId: string;
    items: HeroBanner[];
  } | null>(null);

  useEffect(() => {
    if (!schoolId) return;
    let live = true;
    client.listLiveSchoolBanners(schoolId).then(
      (list) => {
        hadBanners.set(schoolId, list.items.length > 0);
        if (live) setBanners({ schoolId, items: list.items });
      },
      () => {
        // The hero falls back to the school's name; Home stays usable (P-HOME-1).
        hadBanners.set(schoolId, false);
        if (live) setBanners({ schoolId, items: [] });
      },
    );
    return () => {
      live = false;
    };
  }, [client, schoolId]);

  const loaded = banners !== null && banners.schoolId === schoolId;
  const items = loaded ? banners.items : [];

  return (
    <ScrollView style={styles.scroll} testID="home-screen">
      <View style={styles.page} role="main">
        {linked.length > 0 ? (
          <View style={styles.section}>
            <Text tone="muted" weight="semiBold" nativeID="shopping-for">
              {HOME_COPY.shoppingFor}
            </Text>
            <View
              style={styles.children}
              role="radiogroup"
              aria-labelledby="shopping-for"
            >
              {linked.map((s) => {
                const label = HOME_COPY.chip(
                  s.name,
                  s.classLevel ?? "",
                  s.section ?? "",
                );
                const on = s.id === selected?.id;
                return (
                  <PressableTarget
                    key={s.id}
                    accessibilityRole="radio"
                    accessibilityLabel={label}
                    accessibilityState={{ checked: on }}
                    aria-checked={on}
                    onPress={() => selectStudent(s.id)}
                    style={on ? [styles.child, styles.childOn] : styles.child}
                    hoveredStyle={styles.hover}
                  >
                    <View
                      style={
                        on ? [styles.avatar, styles.avatarOn] : styles.avatar
                      }
                    >
                      {on ? (
                        <CheckIcon color={theme.onPrimary} />
                      ) : (
                        <Text weight="bold">
                          {s.name.slice(0, 1).toUpperCase()}
                        </Text>
                      )}
                    </View>
                    <View style={styles.childText}>
                      <Text weight="bold" size="xl">
                        {s.name}
                      </Text>
                      <Text size="sm" tone="muted">
                        {`${standardClassName(s.classLevel ?? "")} · ${s.section ?? ""}`}
                      </Text>
                    </View>
                  </PressableTarget>
                );
              })}
            </View>
          </View>
        ) : null}
        {selected?.school ? (
          <HeroCarousel
            banners={items}
            schoolName={selected.school.name}
            fallbackText={HOME_COPY.heroFallback}
            loading={!loaded}
            expectBanners={hadBanners.get(selected.school.id) ?? true}
          />
        ) : null}
        <View style={styles.section}>
          <Text
            variant="heading"
            size="lg"
            accessibilityRole="header"
            aria-level={2}
          >
            {HOME_COPY.categories}
          </Text>
          <View style={styles.tiles}>
            {CATEGORIES.map((c) => (
              <CategoryTile key={c.id} category={c} />
            ))}
          </View>
        </View>
        {Platform.OS === "web" ? (
          <View style={styles.footer} role="contentinfo">
            <Text size="sm" tone="muted">
              {HOME_COPY.footer}
            </Text>
          </View>
        ) : null}
      </View>
    </ScrollView>
  );
}
